import { supabase } from '../supabase/client';
import type { EventFilters } from '@/types/telemetry';

/**
 * Get telemetry events
 */
export const getEvents = async (filters: EventFilters = {}) => {
  try {
    let query = supabase
      .from('app_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (filters.event_type) query = query.eq('event_type', filters.event_type);
    if (filters.severity) query = query.eq('severity', filters.severity);
    if (filters.user_id) query = query.eq('user_id', filters.user_id);
    if (filters.session_id) query = query.eq('session_id', filters.session_id);
    if (filters.screen) query = query.eq('screen', filters.screen);
    if (filters.date_from) query = query.gte('created_at', filters.date_from);
    if (filters.date_to) query = query.lte('created_at', filters.date_to);

    const { data, error } = await query;

    if (error) {
      console.error('[TelemetryService] Get events error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user journey (events for a specific session)
 */
export const getUserJourney = async (userId: string, sessionId: string) => {
  try {
    const { data, error } = await supabase
      .from('app_events')
      .select('*')
      .eq('user_id', userId)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[TelemetryService] Get journey error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get error summary
 */
export const getErrorSummary = async (timeRange: string = '24h') => {
  try {
    const since = new Date();
    if (timeRange === '24h') since.setHours(since.getHours() - 24);
    else if (timeRange === '7d') since.setDate(since.getDate() - 7);
    else since.setDate(since.getDate() - 30);

    const { data, error } = await supabase
      .from('app_events')
      .select('*')
      .eq('severity', 'error')
      .gte('created_at', since.toISOString());

    if (error) {
      console.error('[TelemetryService] Get error summary error:', error);
      return { success: false, error: error.message };
    }

    // Process data
    const uniqueErrors = new Set((data as any[]).map((e: any) => e.error_message)).size;
    const affectedUsers = new Set((data as any[]).map((e: any) => e.user_id)).size;

    return {
      success: true,
      data: {
        total_errors: data.length,
        unique_errors: uniqueErrors,
        affected_users: affectedUsers,
      },
    };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get events for a specific session with optional filters
 */
export const getSessionEvents = async (
  sessionId: string,
  filters: Partial<EventFilters> = {}
) => {
  try {
    let query = supabase
      .from('app_events')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false })
      .limit(500);

    if (filters.event_type) query = query.eq('event_type', filters.event_type);
    if (filters.severity) query = query.eq('severity', filters.severity);
    if (filters.screen) query = query.eq('screen', filters.screen);
    if (filters.date_from) query = query.gte('created_at', filters.date_from);
    if (filters.date_to) query = query.lte('created_at', filters.date_to);

    const { data, error } = await query;

    if (error) {
      console.error('[TelemetryService] Get session events error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get errors and warnings for a specific user
 */
export const getCustomerEvents = async (
  userId: string,
  filters: Partial<EventFilters> = {},
  limit: number = 100
) => {
  try {
    let query = supabase
      .from('app_events')
      .select('*')
      .eq('user_id', userId)
      .in('severity', ['warn', 'error'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (filters.event_type) query = query.eq('event_type', filters.event_type);
    if (filters.severity) query = query.eq('severity', filters.severity);
    if (filters.screen) query = query.eq('screen', filters.screen);
    if (filters.date_from) query = query.gte('created_at', filters.date_from);
    if (filters.date_to) query = query.lte('created_at', filters.date_to);

    const { data, error } = await query;

    if (error) {
      console.error('[TelemetryService] Get customer events error:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get event summary (counts by severity and type) for a specific user
 */
export const getCustomerEventsSummary = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('app_events')
      .select('severity, event_type')
      .eq('user_id', userId)
      .in('severity', ['warn', 'error']);

    if (error) {
      console.error('[TelemetryService] Get customer events summary error:', error);
      return { success: false, error: error.message };
    }

    // Process data to get counts
    const events = data as { severity: string; event_type: string }[];
    const errorCount = events.filter((e) => e.severity === 'error').length;
    const warnCount = events.filter((e) => e.severity === 'warn').length;
    
    // Count by event type
    const byEventType = events.reduce((acc, e) => {
      const key = `${e.event_type}-${e.severity}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      success: true,
      data: {
        total: events.length,
        errors: errorCount,
        warnings: warnCount,
        byEventType,
      },
    };
  } catch (error: any) {
    console.error('[TelemetryService] Unexpected error:', error);
    return { success: false, error: error.message };
  }
};

export const telemetryService = {
  getEvents,
  getUserJourney,
  getErrorSummary,
  getSessionEvents,
  getCustomerEvents,
  getCustomerEventsSummary,
};

